import { z } from 'zod';
import { ImageFit, ImageRatio } from '../../artifacts/images/values.mjs';
import { requireCondition } from './errors.mjs';

const common = { presence: z.enum(['present', 'absent']), legacy: z.literal('none') };
export const ImageFitPresentation = z
  .strictObject({
    ...common,
    kind: z.literal('imageFit'),
    effectiveDefault: ImageFit,
    cropSafe: z.boolean(),
    available: z.boolean(),
  })
  .refine((value) => value.effectiveDefault !== 'cover' || value.cropSafe, 'image_crop_not_safe');
export const ImageRatioPresentation = z
  .strictObject({
    ...common,
    kind: z.literal('imageRatio'),
    effectiveDefault: ImageRatio,
    allowedRatios: z.array(ImageRatio).min(1).max(5),
  })
  .refine(
    (value) =>
      new Set(value.allowedRatios).size === value.allowedRatios.length &&
      value.allowedRatios.includes(value.effectiveDefault),
    'invalid_allowed_image_ratios',
  );

export function isImageProperty(property) {
  return property === 'imageFit' || property === 'imageRatio';
}
export function imagePresentationEditable(presentation) {
  return presentation?.kind !== 'imageFit' || presentation.available === true;
}
export function requireImagePresentationAvailable(presentation) {
  requireCondition(imagePresentationEditable(presentation), 'image_fit_unavailable');
}
export function normalizeImagePresentation(property, value, slot) {
  const schema = property === 'imageFit' ? ImageFitPresentation : ImageRatioPresentation;
  requireCondition(slot?.presentation?.kind === property, 'slot_presentation_unavailable');
  const metadata = schema.parse(slot.presentation);
  requireImagePresentationAvailable(metadata);
  if (property === 'imageFit') {
    requireCondition(ImageFit.safeParse(value).success, 'invalid_image_fit');
    requireCondition(value !== 'cover' || metadata.cropSafe, 'image_crop_not_safe');
  } else {
    requireCondition(ImageRatio.safeParse(value).success, 'invalid_image_ratio');
    requireCondition(metadata.allowedRatios.includes(value), 'image_ratio_not_allowed');
  }
  return value;
}
